import { useEffect, useState } from 'react';
import { useWidgetData } from '../context';
import { usePlayerStatsRows } from '../usePlayerStatsRows';
import type { PlayerLine } from '../playerLine';
import { ensureKeyframes } from '../../effects/keyframes';
import { serverNow } from '../../time-sync';

// ── Bespoke design: Premium Glass (Full Board) ──────────────────────────
// Pixel-faithful React port of /tmp/sl-premium-glass-v1.html (approved
// mockup). Every class is prefixed `pgfb-` to avoid global collisions; the
// mockup's <style> is emitted once via a component-scoped <style> element.
// Fills 100% × 100% of its parent (the parent provides the 16:9 box).

const SAMPLE_HOME: PlayerLine[] = [
  { no: '23', name: 'CARTER', f: 2, pts: 21, reb: 0, ast: 0 },
  { no: '11', name: 'REED', f: 3, pts: 14, reb: 0, ast: 0 },
  { no: '5', name: 'DIAZ', f: 1, pts: 9, reb: 0, ast: 0 },
  { no: '34', name: 'OWENS', f: 2, pts: 11, reb: 0, ast: 0 },
  { no: '8', name: 'NASH', f: 0, pts: 7, reb: 0, ast: 0 },
];

const SAMPLE_AWAY: PlayerLine[] = [
  { no: '7', name: 'OKO', f: 4, pts: 18, reb: 0, ast: 0 },
  { no: '31', name: 'VANCE', f: 2, pts: 15, reb: 0, ast: 0 },
  { no: '9', name: 'PAUL', f: 3, pts: 12, reb: 0, ast: 0 },
  { no: '21', name: 'HALE', f: 1, pts: 8, reb: 0, ast: 0 },
  { no: '4', name: 'BENN', f: 2, pts: 5, reb: 0, ast: 0 },
];

// Verbatim port of the mockup <style>, every selector prefixed `pgfb-`.
// The .board rule is dropped (the parent provides the 16:9 box); its inner
// layout (gradient/border/padding) is applied to .pgfb-board which now just
// fills the parent. Font-family stacks are inlined so the component renders
// correctly even if the Google Fonts <link> is not present.
const CSS = `
.pgfb-board{
  --body:'Manrope',system-ui,sans-serif;--amber:#ffc24d;--cyan:#4fd6f0;--red:#ff6a5e;--home:#b3324a;--away:#3a6df0;--grn:#5af09a;
  --glass:rgba(255,255,255,.07);--glassbrd:rgba(255,255,255,.16);--ink:#eaf1ff;--sub:#9fb3d6;
  position:absolute;inset:0;width:100%;height:100%;border-radius:18px;overflow:hidden;display:flex;flex-direction:column;
  color:var(--ink);font-family:var(--body);
  background:radial-gradient(ellipse at 50% -8%,#1a2649 0%,#0b1224 52%,#06080f 100%);
  border:1px solid var(--glassbrd);box-shadow:0 40px 110px rgba(0,0,0,.75),inset 0 1px 0 rgba(255,255,255,.06);padding:1.1%}
.pgfb-board *{box-sizing:border-box}
.pgfb-glass{background:var(--glass);border:1px solid var(--glassbrd);border-radius:14px;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.12),0 10px 30px rgba(0,0,0,.35)}
.pgfb-arena{height:7.5%;margin-bottom:1.1%;display:flex;align-items:center;justify-content:center;
  font:700 clamp(11px,2vh,20px)/1 'Oswald',sans-serif;letter-spacing:6px;color:#c9d6f5;text-transform:uppercase}
/* main */
.pgfb-main{height:39%;display:flex;gap:1.1%;margin-bottom:1.1%}
.pgfb-side{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative}
.pgfb-side.pgfb-home{background:linear-gradient(160deg,color-mix(in srgb,var(--home) 42%,transparent),color-mix(in srgb,var(--home) 10%,transparent) 60%,var(--glass))}
.pgfb-side.pgfb-away{background:linear-gradient(160deg,color-mix(in srgb,var(--away) 42%,transparent),color-mix(in srgb,var(--away) 10%,transparent) 60%,var(--glass))}
.pgfb-namebar{height:30%;display:flex;align-items:center;justify-content:space-between;padding:0 6%;border-bottom:1px solid rgba(255,255,255,.12)}
.pgfb-namebar .pgfb-nm{font:800 clamp(15px,3.4vh,28px)/1 var(--body);letter-spacing:.5px}
.pgfb-namebar .pgfb-sub{font:700 clamp(7px,1.3vh,11px)/1 'Oswald';letter-spacing:2px;color:var(--sub)}
.pgfb-scorerow{flex:1;display:flex;align-items:center;justify-content:center;gap:6%;padding:0 5%}
.pgfb-side.pgfb-away .pgfb-scorerow{flex-direction:row-reverse}
.pgfb-logo{width:clamp(34px,11vh,84px);height:clamp(34px,11vh,84px);border-radius:50%;background:rgba(255,255,255,.1);border:1px solid var(--glassbrd);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;font:800 clamp(16px,4.4vh,38px)/1 var(--body);flex-shrink:0;overflow:hidden}
.pgfb-logo img{width:100%;height:100%;object-fit:contain}
.pgfb-score{font:800 clamp(48px,17vh,124px)/.8 'Oswald',sans-serif;color:#f4f8ff;text-shadow:0 0 30px rgba(150,190,255,.5),0 4px 12px rgba(0,0,0,.4)}
.pgfb-possline{height:6px;width:100%;background:transparent;transition:background .2s}
.pgfb-possline.pgfb-on{background:var(--amber);box-shadow:0 0 18px rgba(255,194,77,.8),0 -3px 14px rgba(255,194,77,.4)}
/* center */
.pgfb-center{width:25%;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;gap:3px;padding:1.2% 0}
.pgfb-clk{font:800 clamp(34px,11.5vh,92px)/.9 'Oswald',sans-serif;color:#fff;letter-spacing:1px;text-shadow:0 0 26px rgba(120,180,255,.55)}
.pgfb-shotwrap{display:flex;flex-direction:column;align-items:center;margin-top:2%}
.pgfb-shotwrap b{font:700 clamp(8px,1.4vh,12px)/1 'Oswald';color:var(--red);letter-spacing:2px}
.pgfb-shotwrap span{font:800 clamp(22px,6vh,44px)/.95 'Oswald',sans-serif;color:var(--red);text-shadow:0 0 18px rgba(255,106,94,.5)}
.pgfb-poss{display:flex;align-items:center;gap:9px;margin-top:3%}
.pgfb-poss .pgfb-arr{font:700 clamp(12px,2.6vh,22px)/1 var(--body);color:rgba(255,255,255,.18)}
.pgfb-poss .pgfb-arr.pgfb-on{color:var(--amber);text-shadow:0 0 14px rgba(255,194,77,.8)}
.pgfb-poss b{font:700 clamp(7px,1.2vh,10px)/1 'Oswald';letter-spacing:2px;color:var(--sub)}
.pgfb-per{margin-top:3%;font:700 clamp(10px,1.9vh,15px)/1 'Oswald',sans-serif;letter-spacing:3px;color:var(--ink);background:rgba(255,255,255,.08);padding:3px 14px;border-radius:99px;border:1px solid var(--glassbrd)}
/* fouls/tol */
.pgfb-led{height:13%;display:flex;gap:1.1%;margin-bottom:1.1%}
.pgfb-led .pgfb-grp{flex:1;display:flex;align-items:center;justify-content:center;gap:8%}
.pgfb-led .pgfb-cc{width:25%;display:flex;align-items:center;justify-content:center}
.pgfb-wm{font:700 11px/1 'Oswald';color:var(--sub);letter-spacing:4px;opacity:.6}
.pgfb-stat{display:flex;flex-direction:column;align-items:center;line-height:1}
.pgfb-stat b{font:700 clamp(7px,1.2vh,11px)/1 'Oswald';letter-spacing:2px;color:var(--sub)}
.pgfb-stat span{font:800 clamp(18px,5vh,36px)/1 'Oswald',sans-serif}
.pgfb-amberv{color:var(--amber);text-shadow:0 0 14px rgba(255,194,77,.5)}
.pgfb-cyanv{color:var(--cyan);text-shadow:0 0 14px rgba(79,214,240,.5)}
.pgfb-bonus{font:800 clamp(8px,1.3vh,12px)/1 'Oswald';color:#3a1500;background:var(--amber);padding:4px 9px;border-radius:99px;letter-spacing:1px}
/* player tables */
.pgfb-pts{height:21.5%;display:flex;gap:1.1%;margin-bottom:1.1%}
.pgfb-ptbl{flex:1;display:flex;flex-direction:column;padding:.7% 2%}
.pgfb-ptbl .pgfb-cap{display:grid;grid-template-columns:13% 1fr 16% 18%;font:700 clamp(8px,1.5vh,13px)/1 'Oswald';letter-spacing:1px;color:var(--sub);padding-bottom:3px}
.pgfb-ptbl .pgfb-cap span:nth-child(1),.pgfb-ptbl .pgfb-cap span:nth-child(3),.pgfb-ptbl .pgfb-cap span:nth-child(4){text-align:center}
.pgfb-ptbl .pgfb-row{display:grid;grid-template-columns:13% 1fr 16% 18%;align-items:center;flex:1;font:700 clamp(10px,2.2vh,18px)/1 var(--body);border-top:1px solid rgba(255,255,255,.07)}
.pgfb-ptbl .pgfb-row .pgfb-no{color:var(--amber);font-family:'Oswald';font-size:.8em;text-align:center}
.pgfb-ptbl .pgfb-row .pgfb-ptv{color:var(--grn);text-align:center;text-shadow:0 0 10px rgba(90,240,154,.4)}
.pgfb-ptbl .pgfb-row .pgfb-fv{color:var(--red);text-align:center}
/* two ad slots */
.pgfb-ads{height:10.5%;display:flex;gap:1.1%;margin-bottom:1.1%}
.pgfb-ad{flex:1;border:1px dashed rgba(255,255,255,.18);border-radius:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;
   background:rgba(255,255,255,.03);font:700 clamp(8px,1.6vh,13px)/1 'Oswald';letter-spacing:2px;color:var(--sub)}
.pgfb-ad small{font-size:.7em;color:#5a6a8c;letter-spacing:1px;margin-top:3px}
/* Q4 sponsored win prob */
.pgfb-winprob{height:8%;display:flex;flex-direction:column;justify-content:center;gap:5px;padding:0 5%}
.pgfb-winprob .pgfb-lbls{display:flex;justify-content:space-between;align-items:center;font:700 clamp(7px,1.3vh,11px)/1 'Oswald';letter-spacing:1px}
.pgfb-winprob .pgfb-spon{color:var(--sub)}
.pgfb-winprob .pgfb-spon b{color:var(--amber)}
.pgfb-winprob .pgfb-bar{height:7px;border-radius:99px;overflow:hidden;display:flex;background:rgba(58,109,240,.45)}
.pgfb-winprob .pgfb-bar>i{display:block;height:100%;background:linear-gradient(90deg,#ff7d8f,var(--home));box-shadow:0 0 16px rgba(255,120,140,.6)}
`;

// Mirrors ShotClockWidget (widgets.tsx): reads shotClock/shotClockRunning/
// shotClockStartedAt from sportState and counts down live using serverNow().
// Sample/no-state with no running clock → returns raw (defaults to 18 in
// sample mode, 24 in live mode).
function useShotClock(): number {
  const { state, isSample } = useWidgetData();
  const ss = (state?.sportState ?? {}) as Record<string, unknown>;
  const raw = typeof ss.shotClock === 'number' ? (ss.shotClock as number) : isSample ? 18 : 24;
  const running = ss.shotClockRunning === true;
  const startedAt =
    typeof ss.shotClockStartedAt === 'string'
      ? (ss.shotClockStartedAt as string)
      : null;
  const [, force] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running, startedAt]);
  return running && startedAt
    ? Math.max(0, raw - Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000))
    : raw;
}

// Replicates GameClockWidget's useLiveClock (widgets.tsx): if no clock or
// not running → static m:s; if running → remaining time from startedAt.
// Returns the M:SS formatted string. Sample/no-state → '2:14'.
function useClockText(): string {
  const { state, isSample } = useWidgetData();
  const c = state?.clock;
  const [, force] = useState(0);
  useEffect(() => {
    if (!c?.running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [c?.running, c?.startedAt]);

  if (!c) return isSample ? '2:14' : '0:00';
  let m = c.m;
  let s = c.s;
  if (c.running && c.startedAt) {
    const elapsed = Math.max(
      0,
      Math.floor((serverNow() - new Date(c.startedAt).getTime()) / 1000),
    );
    const total = Math.max(0, c.m * 60 + c.s - elapsed);
    m = Math.floor(total / 60);
    s = total % 60;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function PremiumGlassFullBoard() {
  const { snapshot, state, isSample } = useWidgetData();

  useEffect(() => {
    ensureKeyframes();
  }, []);

  const home = usePlayerStatsRows('home', snapshot, state, isSample, SAMPLE_HOME);
  const away = usePlayerStatsRows('away', snapshot, state, isSample, SAMPLE_AWAY);

  const homeScore = state?.home?.score ?? (isSample ? 62 : 0);
  const awayScore = state?.away?.score ?? (isSample ? 58 : 0);

  const homeName = snapshot?.rosters.home.name ?? (isSample ? 'HAWKS' : 'HOME');
  const awayName = snapshot?.rosters.away.name ?? (isSample ? 'LIONS' : 'GUEST');

  const homeColor = snapshot?.rosters.home.colorPrimary ?? '#b3324a';
  const awayColor = snapshot?.rosters.away.colorPrimary ?? '#3a6df0';

  const homeLogo = snapshot?.rosters.home.logoUrl ?? null;
  const awayLogo = snapshot?.rosters.away.logoUrl ?? null;

  const period = state?.period ?? 4;
  const possHome = state?.possession === 'home';
  const possAway = state?.possession === 'away';

  const homeFouls = state?.home?.fouls ?? (isSample ? 4 : 0);
  const awayFouls = state?.away?.fouls ?? (isSample ? 6 : 0);
  const homeTol = state?.home?.timeoutsRemaining ?? (isSample ? 3 : 0);
  const awayTol = state?.away?.timeoutsRemaining ?? (isSample ? 2 : 0);

  // Mockup shows BONUS on the away side only; in sample mode mirror that.
  const homeBonus = isSample ? false : state?.home?.bonus === true;
  const awayBonus = isSample ? true : state?.away?.bonus === true;

  const winHome = isSample
    ? 58
    : Math.max(0, Math.min(100, state?.winProbHome ?? 50));
  const winAway = 100 - winHome;

  const shot = useShotClock();

  const clockText = useClockText();

  const arena = isSample
    ? 'RIVERSIDE FIELDHOUSE'
    : snapshot?.venueName ?? 'ARENA';

  const homeFirst = (homeName[0] ?? 'H').toUpperCase();
  const awayFirst = (awayName[0] ?? 'A').toUpperCase();

  return (
    <div className="pgfb-board" style={{ ['--home' as string]: homeColor, ['--away' as string]: awayColor }}>
      <style>{CSS}</style>

      <div className="pgfb-arena">{arena}</div>

      <div className="pgfb-main">
        {/* HOME side */}
        <div className="pgfb-side pgfb-home pgfb-glass">
          <div className="pgfb-namebar">
            <span className="pgfb-nm">{homeName}</span>
            <span className="pgfb-sub">HOME</span>
          </div>
          <div className="pgfb-scorerow">
            <div className="pgfb-logo">
              {homeLogo ? <img src={homeLogo} alt={homeName} /> : homeFirst}
            </div>
            <div className="pgfb-score">{homeScore}</div>
          </div>
          <div className={`pgfb-possline${possHome ? ' pgfb-on' : ''}`} />
        </div>

        {/* CENTER column */}
        <div className="pgfb-center pgfb-glass">
          <div className="pgfb-clk">{clockText}</div>
          <div className="pgfb-shotwrap">
            <b>SHOT CLOCK</b>
            <span>{shot}</span>
          </div>
          <div className="pgfb-poss">
            <span className={`pgfb-arr${possHome ? ' pgfb-on' : ''}`}>&#9664;</span>
            <b>POSS</b>
            <span className={`pgfb-arr${possAway ? ' pgfb-on' : ''}`}>&#9654;</span>
          </div>
          <div className="pgfb-per">PERIOD {period}</div>
        </div>

        {/* AWAY side */}
        <div className="pgfb-side pgfb-away pgfb-glass">
          <div className="pgfb-namebar">
            <span className="pgfb-nm">{awayName}</span>
            <span className="pgfb-sub">GUEST</span>
          </div>
          <div className="pgfb-scorerow">
            <div className="pgfb-logo">
              {awayLogo ? <img src={awayLogo} alt={awayName} /> : awayFirst}
            </div>
            <div className="pgfb-score">{awayScore}</div>
          </div>
          <div className={`pgfb-possline${possAway ? ' pgfb-on' : ''}`} />
        </div>
      </div>

      {/* LED row: fouls/TOL + SCORELYNC chip + bonus */}
      <div className="pgfb-led">
        <div className="pgfb-grp pgfb-glass">
          <div className="pgfb-stat">
            <b>FOULS</b>
            <span className="pgfb-amberv">{homeFouls}</span>
          </div>
          <div className="pgfb-stat">
            <b>T.O.L</b>
            <span className="pgfb-cyanv">{homeTol}</span>
          </div>
          {homeBonus && <span className="pgfb-bonus">BONUS</span>}
        </div>
        <div className="pgfb-cc pgfb-glass">
          <span className="pgfb-wm">SCORELYNC</span>
        </div>
        <div className="pgfb-grp pgfb-glass">
          {awayBonus && <span className="pgfb-bonus">BONUS</span>}
          <div className="pgfb-stat">
            <b>T.O.L</b>
            <span className="pgfb-cyanv">{awayTol}</span>
          </div>
          <div className="pgfb-stat">
            <b>FOULS</b>
            <span className="pgfb-amberv">{awayFouls}</span>
          </div>
        </div>
      </div>

      {/* Player tables */}
      <div className="pgfb-pts">
        <div className="pgfb-ptbl pgfb-glass pgfb-home">
          <div className="pgfb-cap">
            <span>#</span>
            <span>HOME &mdash; ON COURT</span>
            <span>F</span>
            <span>PTS</span>
          </div>
          {home.rows.map(({ id, line }, i) => (
            <div
              key={id || `home-${i}`}
              className="pgfb-row"
              data-player-id={id}
              style={{
                ['--row-flash' as string]: home.flashColor,
                animation:
                  home.flashPlayerId === id && id
                    ? 'slPlayerFlash 1.6s ease-out'
                    : undefined,
              }}
            >
              <span className="pgfb-no">{line.no}</span>
              <span>{line.name}</span>
              <span className="pgfb-fv">{line.f}</span>
              <span className="pgfb-ptv">{line.pts}</span>
            </div>
          ))}
        </div>
        <div className="pgfb-ptbl pgfb-glass pgfb-away">
          <div className="pgfb-cap">
            <span>#</span>
            <span>AWAY &mdash; ON COURT</span>
            <span>F</span>
            <span>PTS</span>
          </div>
          {away.rows.map(({ id, line }, i) => (
            <div
              key={id || `away-${i}`}
              className="pgfb-row"
              data-player-id={id}
              style={{
                ['--row-flash' as string]: away.flashColor,
                animation:
                  away.flashPlayerId === id && id
                    ? 'slPlayerFlash 1.6s ease-out'
                    : undefined,
              }}
            >
              <span className="pgfb-no">{line.no}</span>
              <span>{line.name}</span>
              <span className="pgfb-fv">{line.f}</span>
              <span className="pgfb-ptv">{line.pts}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Two ad slots */}
      <div className="pgfb-ads">
        <div className="pgfb-ad pgfb-glass">
          ADVERTISER 1<small>728 x 90 banner</small>
        </div>
        <div className="pgfb-ad pgfb-glass">
          ADVERTISER 2<small>728 x 90 banner</small>
        </div>
      </div>

      {/* Sponsored win probability */}
      <div className="pgfb-winprob pgfb-glass">
        <div className="pgfb-lbls">
          <span style={{ color: '#ff9aa7' }}>
            {homeName} {winHome}%
          </span>
          <span className="pgfb-spon">
            WIN PROBABILITY &middot; presented by <b>STATELINE BANK</b>
          </span>
          <span style={{ color: '#9ec2ff' }}>
            {winAway}% {awayName}
          </span>
        </div>
        <div className="pgfb-bar">
          <i style={{ width: `${winHome}%` }} />
        </div>
      </div>
    </div>
  );
}
